import React, { useCallback, useEffect } from 'react';

import { ViewStyle } from 'react-native';

import { useTranslation } from 'react-i18next';
import Animated, {
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { StripeArrow } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { ContentType } from 'services/data-manager/types';

import SvgButton from '../SvgButton';
import styles from './styles';

export enum EArrowType {
    BWD = 'bwd',
    FWD = 'fwd',
}

type Props = {
    type: EArrowType;
    onArrowNavigationPress: (type: EArrowType) => void;
    hasTitle: boolean;
    width: number;
    metadataType: ContentType;
    testID?: string;
    railId?: string;
    style?: ViewStyle | ViewStyle[];
    tabIndex?: number;
};

const ARROW_ICON_SIZE = {
    width: 18,
    height: 34,
};

const ANIMATION_DURATION = 300;

const ArrowButton = ({
    type,
    onArrowNavigationPress,
    hasTitle,
    width,
    metadataType,
    testID,
    railId,
    style,
    tabIndex = -1,
}: Props) => {
    const { t } = useTranslation();

    const {
        ACCESSIBILITY: {
            ARROWS: {
                ACCESSIBILITY_WEB_LEFT_ARROW,
                ACCESSIBILITY_WEB_RIGHT_ARROW,
            },
        },
    } = LANGUAGE_KEYS;

    const handlePress = useCallback(() => {
        onArrowNavigationPress(type);
    }, [type, onArrowNavigationPress]);

    const animatedWidth = useSharedValue(width);

    const animatedStyle = useAnimatedStyle(() => {
        return {
            width: animatedWidth.value,
        };
    });

    const isCastStripe = metadataType === ContentType.CastPerson;

    useEffect(() => {
        animatedWidth.value = withTiming(width, {
            duration: ANIMATION_DURATION,
        });
    }, [width]);

    return (
        <Animated.View
            style={[
                styles.container,
                hasTitle &&
                    type === EArrowType.BWD &&
                    styles.containerWithTopMargin,
                hasTitle &&
                    type === EArrowType.FWD &&
                    styles.containerWithBottomMargin,
                type === EArrowType.BWD ? styles.bwdButton : styles.fwdButton,
                animatedStyle,
                isCastStripe && styles.castStripe,
                style,
            ]}
            dataSet={{ class: `arrow_${type}`, id: railId || '' }}
        >
            <SvgButton
                tabIndex={tabIndex}
                accessible
                accessibilityLabel={
                    type === EArrowType.BWD
                        ? t(ACCESSIBILITY_WEB_LEFT_ARROW)
                        : t(ACCESSIBILITY_WEB_RIGHT_ARROW)
                }
                testID={testID}
                style={styles.arrowButton}
                Svg={StripeArrow}
                svgStyle={ARROW_ICON_SIZE}
                onPress={handlePress}
                accessibilityRole="button"
            />
        </Animated.View>
    );
};

export default ArrowButton;
